import React, { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";

// ─── API SHAPE (SalesOrdersController GetAll) ─────────────────────────────────
//
// Header fields (ALL FLAT — no nested customer object):
//   customerName, shippingAddress, locationName, date,
//   approverName, quoteNumber, vatAmount, ewtAmount
//   totalAmount  ← DO NOT USE — it is ALREADY REDUCED by rejections
//                  e.g. fully-rejected SO shows totalAmount = 0
//
// salesOrderItems[]:
//   quantity          ← full ordered qty (never reduced)
//   unitPrice
//   lineTotal         ← full line total (qty × unitPrice, never reduced)
//   rejectedQuantity  ← how many units were rejected
//   itemCode          ← enriched by backend (Products.ItemCode)
//   unitName          ← enriched by backend (UnitOfMeasurements.UnitName)
//   deliveredQty      ← computed
//   remainingQty      ← computed
//
// WHY WE RECOMPUTE THE TOTAL:
//   data.totalAmount on a fully-rejected SO = ₱0.00
//   The list shows ₱0 (current) with ~~₱3,600~~ (original) strikethrough.
//   The PRINT must show the full breakdown:
//     Subtotal (full) → minus Rejected → ± VAT/EWT → Grand Total
// ─────────────────────────────────────────────────────────────────────────────

export const SalesOrderPrint = React.forwardRef(({ data }, ref) => {
  const barcodeRef = useRef(null);
  const soNumber = data?.salesOrderNumber;

  useEffect(() => {
    if (soNumber && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, soNumber, {
          format: "CODE128",
          lineColor: "#000000",
          width: 1.5,
          height: 40,
          displayValue: false,
          margin: 0,
        });
      } catch (e) {
        console.error("Barcode error", e);
      }
    }
  }, [soNumber]);

  if (!data) return null;

  const items = data.salesOrderItems || [];
  const vatAmount = data.vatAmount || 0;
  const ewtAmount = data.ewtAmount || 0;
  const quoteNumber = data.quoteNumber;
  const approverName = data.approverName;

  // ── Recompute totals from line items ──────────────────────────────────────
  // item.lineTotal from the API = qty × unitPrice (full, NOT rejection-adjusted)
  const subtotal = items.reduce(
    (s, i) => s + (i.lineTotal ?? (i.quantity || 0) * (i.unitPrice || 0)),
    0,
  );

  // Amount lost to rejections
  const rejectedAmount = items.reduce(
    (s, i) => s + (i.rejectedQuantity || 0) * (i.unitPrice || 0),
    0,
  );

  // Correct grand total = full subtotal − rejected + VAT − EWT
  const grandTotal = subtotal - rejectedAmount + vatAmount - ewtAmount;

  const totalQty = items.reduce((s, i) => s + (i.quantity || 0), 0);
  const hasDeductions = rejectedAmount > 0 || vatAmount > 0 || ewtAmount > 0;

  const fmt = (v) =>
    new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(v || 0);

  return (
    <div
      ref={ref}
      className="p-8 bg-white text-black text-xs font-sans max-w-[800px] mx-auto"
    >
      {/* ── HEADER ── */}
      <div className="text-center mb-6">
        <h1 className="text-xl font-bold uppercase">Ichthus Technology</h1>
        <div className="text-[10px] text-gray-600">
          <p># 3128 Sto. Rosario Street, Barangay</p>
          <p>Mapulang Lupa, Valenzuela, 1440 Metro</p>
        </div>
        <div className="mt-2 flex justify-center">
          <svg ref={barcodeRef}></svg>
        </div>
        <p className="text-[9px] mt-1 tracking-widest">{soNumber}</p>
      </div>

      {/* ── INFO GRID ── */}
      <div className="flex justify-between items-start mb-6 gap-4">
        {/* Bill To — uses flat API fields (no nested customer object) */}
        <div className="w-1/2">
          <h2 className="font-bold text-sm uppercase mb-1 bg-gray-100 p-1">
            Bill To:
          </h2>
          <div className="pl-1 space-y-0.5">
            <p className="font-bold uppercase text-sm">
              {data.customerName || "WALK-IN CUSTOMER"}
            </p>
            <p className="uppercase w-3/4">
              {data.shippingAddress || "No Address Provided"}
            </p>
          </div>
        </div>

        {/* SO Details */}
        <div className="w-1/2">
          <h2 className="font-bold text-lg mb-2 text-right">SALES ORDER</h2>
          <div className="grid grid-cols-2 gap-y-1 text-xs text-right">
            <span className="font-bold text-gray-600">Sales Order No.</span>
            <span className="font-semibold">{soNumber}</span>

            {quoteNumber && (
              <>
                <span className="font-bold text-gray-600">Quotation Ref.</span>
                <span className="font-semibold">{quoteNumber}</span>
              </>
            )}

            <span className="font-bold text-gray-600">Date</span>
            <span>
              {data.date ? new Date(data.date).toLocaleDateString() : "—"}
            </span>

            <span className="font-bold text-gray-600">Branch / Location</span>
            <span>{data.locationName || "Main Office"}</span>

            <span className="font-bold text-gray-600">Approver</span>
            <span>{approverName || "N/A"}</span>
          </div>

          <div className="mt-4 bg-gray-100 p-2 border border-gray-200">
            <div className="flex justify-between font-bold">
              <span>Total Items</span>
              <span>{totalQty} units</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── TABLE HEADER ── */}
      <div className="mt-4 border-b-2 border-black pb-1 mb-1 bg-gray-100 flex font-bold text-xs uppercase p-1">
        <div className="w-6">#</div>
        <div className="w-24">Item Code</div>
        <div className="flex-1">Description</div>
        <div className="w-24 text-center">Qty / UOM</div>
        <div className="w-24 text-right">Unit Price</div>
        <div className="w-24 text-right">Total</div>
      </div>

      {/* ── TABLE BODY ── */}
      <div className="min-h-[300px] font-sans tracking-tight">
        <div className="divide-y divide-gray-100 border-b border-gray-100">
          {items.map((item, index) => {
            const qty = item.quantity || 0;
            const price = item.unitPrice || 0;
            // lineTotal from API = full amount (qty × price), not adjusted
            const lineTotal = item.lineTotal ?? qty * price;
            const rejected = item.rejectedQuantity || 0;

            return (
              <div
                key={item.id || index}
                className="flex items-start py-1.5 text-[10px] uppercase font-bold leading-snug"
              >
                <div className="w-6 text-center text-gray-300 pt-0.5">
                  {(index + 1).toString().padStart(2, "0")}
                </div>

                {/* itemCode — enriched by backend (Products.ItemCode) */}
                <div className="w-24 text-indigo-500 truncate px-1 pt-0.5 font-mono">
                  {item.itemCode || "—"}
                </div>

                <div className="flex-1 px-1">
                  <div className="text-gray-800">{item.productName}</div>
                  {/* ← REJECTION INDICATOR per line */}
                  {rejected > 0 && (
                    <div className="text-[9px] text-red-500 font-semibold normal-case mt-0.5 flex items-center gap-0.5">
                      ⚠ {rejected} unit{rejected !== 1 ? "s" : ""} rejected
                      <span className="text-red-400">
                        (−{fmt(rejected * price)})
                      </span>
                    </div>
                  )}
                </div>

                {/* qty + unitName — unitName enriched by backend (UnitOfMeasurements.UnitName) */}
                <div className="w-24 flex justify-center items-start gap-0.5 pt-0.5">
                  <span className="text-gray-900">{qty}</span>
                  {item.unitName && (
                    <span className="text-gray-400 text-[9px] ml-0.5 normal-case">
                      {item.unitName}
                    </span>
                  )}
                </div>

                <div className="w-24 text-right text-gray-500 tabular-nums px-1 pt-0.5">
                  {fmt(price)}
                </div>

                <div className="w-24 text-right text-gray-900 tabular-nums pl-1 pr-2 pt-0.5">
                  {fmt(lineTotal)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── TOTALS ── */}
      <div className="mt-4 border-t-2 border-black pt-2 flex justify-end">
        <div className="w-1/2 space-y-1">
          {/* Show subtotal row only when there are deductions */}
          {hasDeductions && (
            <div className="flex justify-between text-xs text-gray-600 px-2">
              <span>Subtotal:</span>
              <span>{fmt(subtotal)}</span>
            </div>
          )}

          {/* Rejection deduction row */}
          {rejectedAmount > 0 && (
            <div className="flex justify-between text-xs font-bold px-2 py-1 bg-red-50 rounded text-red-600 border border-red-100">
              <span>Rejected Items:</span>
              <span>− {fmt(rejectedAmount)}</span>
            </div>
          )}

          {vatAmount > 0 && (
            <div className="flex justify-between text-xs text-gray-600 px-2">
              <span>VAT (12%):</span>
              <span>{fmt(vatAmount)}</span>
            </div>
          )}

          {ewtAmount > 0 && (
            <div className="flex justify-between text-xs text-gray-600 px-2">
              <span>EWT (1%):</span>
              <span>− {fmt(ewtAmount)}</span>
            </div>
          )}

          <div className="flex justify-between text-sm font-bold bg-gray-100 p-2 rounded">
            <span>Total Amount:</span>
            <span>{fmt(grandTotal)}</span>
          </div>
        </div>
      </div>

      {/* ── SIGNATURES ── */}
      <div className="mt-16 grid grid-cols-3 gap-8 text-[10px]">
        {[
          "Prepared By:",
          "Checked By:",
          "Approved By / Customer Conforme:",
        ].map((label) => (
          <div key={label}>
            <div className="border-b border-black mb-1"></div>
            <p className="font-bold">{label}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 text-center text-[9px] text-gray-400">
        System Generated Sales Order ({soNumber}) | Printed on{" "}
        {new Date().toLocaleString()}
      </div>
    </div>
  );
});

export default SalesOrderPrint;
